/**
 * `N inputs needed` — the button that stands where a figure would be, and the
 * popover behind it (VRG-459).
 *
 * ## TWO TRIGGERS, ONE PANEL
 *
 * `MetricInputsNeededButton` stands in for a figure the metric does not have yet.
 * `MetricAssumptionMarker` sits BESIDE a figure it already has, to disclose that the
 * figure rests on a supplied input and to let the reader change it. Both open the
 * same `MetricInputsPanel`, so there is one form for this resource rather than two
 * that drift — see that component and §36.
 *
 * ## ⚠️ WHICH CARDS GET THE BUTTON, AND WHY THE TEST IS NOT "HAS NO VALUE"
 *
 * A card renders this ONLY when the ROI response withholds its metric with reason
 * `needs_input`. Two other empty states exist and must NOT get it:
 *
 *   `source_not_instrumented`  the registry's `blocked` — Vera does not capture the
 *                              events at all. Keeps its em-dash and its stated
 *                              reason.
 *   `not_computed`             every input is set and Vera still cannot produce the
 *                              measured half of the formula. Also keeps the em-dash.
 *
 * Offering "add inputs" on either is worse than the em-dash: it sends a brand to a
 * form, has them enter a number, and leaves the metric exactly as withheld as
 * before — which is the specific experience that makes people stop trusting a
 * dashboard. The server draws this distinction for us (`IRoiSummary.withheld[]`
 * carries the reason and the exact `missing_inputs`), so nothing here re-derives it.
 *
 * ## Why a popover and not a card flip or an inline field
 *
 * A tile is 160px and holds a title, a figure and a basis line. It cannot hold a
 * label, a field, an inline unit, a parsed echo, a consequence and a save button —
 * for one input, let alone two. A flip would hide the number the reader is looking
 * at. A popover leaves the card in place and puts the form beside it.
 *
 * ## The count is IN the label, deliberately
 *
 * "2 inputs needed", not "Field inputs needed". One input is a very different
 * prompt from four, and a reader deciding whether to start deserves to know which
 * before they click.
 */
import { useMemo, useState } from 'react'

import {
  Box,
  Divider,
  IconButton,
  Link,
  Popover,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { ArrowUpRightIcon, SlidersHorizontalIcon } from 'lucide-react'
import { Link as RouterLink } from 'react-router-dom'

import { Button, MENU_PAPER_SX, dimNeutralText, menuPaperChromeSx } from '@repo/ui'
import type { IRoiSummary, MetricInputKey } from '@repo/types'

import { useMetricInputs, useSetMetricInputs } from 'hooks/api'
import { useReferrerState } from 'hooks'
import { ROUTES } from 'routes'

import { DEFAULT_REPORTING_CURRENCY } from '@repo/types'

import { DEFAULT_COMPANY_ID } from 'config'
import { useCompany } from 'hooks/api'

import { MetricInputField } from './MetricInputField'
import {
  metricAssumptionState,
  valueForKey,
} from './metricInputFormat'

/**
 * The inputs a metric is waiting on, or null when it is not waiting on any.
 *
 * Exported because the CARD needs to know whether to render a figure or this
 * button, and that decision belongs to the same rule the popover enforces.
 */
export const missingInputsFor = (
  roi: IRoiSummary | null | undefined,
  metricId: string | undefined,
): MetricInputKey[] | null => {
  if (!roi || !metricId) return null
  const entry = roi.withheld.find(w => w.metric_id === metricId)
  // `reason === 'needs_input'` and NOT `missing_inputs.length > 0`. They agree today
  // and are different claims: the reason is the server's judgement about whether the
  // brand can do anything, and it is the one this UI must obey.
  if (!entry || entry.reason !== 'needs_input') return null
  return entry.missing_inputs
}

interface MetricInputsPopoverProps {
  /** Registry metric id — the key `withheld[]` and `contributions[]` are keyed on. */
  metricId: string
  /** The card's title, so the popover can name what it is unblocking. */
  metricTitle: string
  /** The keys this metric is waiting on, from `missingInputsFor`. */
  missing: MetricInputKey[]
  /** The ROI response the card is rendering from, for the consequence echo. */
  roi: IRoiSummary | null
  /**
   * Refetch the ROI so `Partial · N of M sources` and the multiple move without a
   * reload. The change IS the feedback, so this fires on every successful save.
   */
  onSaved?: () => void
}

interface MetricInputsPanelProps {
  /** DOM id, so the trigger's `aria-controls` resolves. */
  id: string
  /** Null closes it. The trigger owns this state; the panel only reads it. */
  anchorEl: HTMLElement | null
  onClose: () => void
  metricId: string
  metricTitle: string
  /**
   * The keys to render, when the caller knows them — the `needs_input` case, where
   * the server named exactly what is missing.
   *
   * OMITTED means "every input this metric declares", which is the ASSUMPTION case:
   * the metric already has a figure and the reader is here to adjust the input it
   * rests on, so there is nothing missing to filter by. Derived from the catalogue's
   * own `metric_ids` rather than from the registry, for the reason `useMetricInputs`
   * gives at length — the fields a form renders must be the ones the write endpoint
   * validates against, not a local copy of them.
   */
  missing?: MetricInputKey[]
  /** The sentence under the heading. Different question in each mode; see callers. */
  intro: string
  roi: IRoiSummary | null
  onSaved?: () => void
}

/**
 * The panel itself — heading, intro, the fields, the save feedback, the way out.
 *
 * Extracted from `MetricInputsNeededButton` when the assumption marker needed the
 * SAME popover from a different trigger (§36: the second hand-roll is a shared
 * component). Only the trigger and the intro differ between the two; everything a
 * reader interacts with is this one component, so the two entry points cannot drift
 * into two different forms for one resource.
 */
const MetricInputsPanel = ({
  id,
  anchorEl,
  onClose,
  metricId,
  metricTitle,
  missing,
  intro,
  roi,
  onSaved,
}: MetricInputsPanelProps) => {
  const theme = useTheme()

  // §42 — a lateral jump between surfaces carries its origin, so the settings page
  // renders "< Back to Analytics" and a reader who left a card to fix a figure can get
  // back to the card. Wired at the same time as the `to=`, which is the whole point of
  // the rule: the opt-in version of this was forgotten by 35 of 37 call sites.
  const referrer = useReferrerState('Analytics')

  // Only fetched once the popover is opened. The catalogue is not needed to decide
  // whether to render the TRIGGER — that comes from the ROI response the card
  // already has — so a closed card costs no request, and a page showing three
  // needs-input cards does not fire three.
  const { catalogue, values } = useMetricInputs({ enabled: Boolean(anchorEl) })
  const setInputs = useSetMetricInputs()

  const [savedNote, setSavedNote] = useState<string | null>(null)

  /*
    The company's CURRENT reporting currency — what a NEW figure defaults to.

    Read from the COMPANY, not from `roi`: `reporting_currency` lives on `IRoiMetrics`,
    and this popover is handed an `IRoiSummary`, which is a different shape. Sourcing it
    from the summary compiled only because `roi` is loosely typed at the call site.
  */
  const { data: company } = useCompany({ id: DEFAULT_COMPANY_ID })
  const reportingCurrency = company?.reporting_currency ?? DEFAULT_REPORTING_CURRENCY

  const fields = useMemo(
    () =>
      missing
        ? catalogue.filter(d => missing.includes(d.key))
        : catalogue.filter(d => d.metric_ids.includes(metricId)),
    [catalogue, missing, metricId],
  )

  const handleSave = async (key: MetricInputKey, storedValue: number, currency: string) => {
    const definition = catalogue.find(d => d.key === key)
    const response = await setInputs.mutateAsync([
      {
        key,
        value: storedValue,
        /*
          The currency the FIELD was denominated in, passed up from `MetricInputField`
          — NOT the company's current reporting currency, and no longer a hardcoded
          'USD'. A figure is stored in the unit it was ENTERED in; re-labelling a
          stored 50000 as £500.00 because the company later switched is the read-time
          relabelling this whole change exists to prevent (VRG-497).

          Still sent unconditionally rather than branching: the server ignores it where
          it does not apply, and a missing currency is a 400 rather than a default.
        */
        currency,
      },
    ])

    /*
      ⚠️ THE COPY REPORTS WHAT WAS SAVED, NOT WHAT NOW COMPUTES.

      `unblocked_metric_ids` means "has every input it declares" — which is NOT the
      same as "renders a figure". A metric can appear there and still be withheld as
      `not_computed` on the very next ROI response, because Vera cannot produce the
      measured half of its formula. So this says the metric has what it asked for,
      and the refetched card is what says whether a number appeared. Promising the
      second from the first is precisely the "entered a number, nothing changed"
      experience this feature exists to avoid.
    */
    const unblocked = response.unblocked_metric_ids
    setSavedNote(
      unblocked.length > 1
        ? `Saved. ${unblocked.length} metrics now have every figure they asked for.`
        : `Saved${definition ? ` ${definition.label.toLowerCase()}` : ''}.`,
    )

    onSaved?.()
  }

  return (
      <Popover
        id={id}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          onClose()
          setSavedNote(null)
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        /*
          ── THE PAPER IS THE HOUSE DROPDOWN SURFACE, FROM THE SHARED CONSTANTS ───────

          This used to hand-roll `mt: 2`, `borderRadius: 3` and a `primary[100]` border
          and set NO background behaviour — so it rendered as a pale grey panel against
          a near-black page while every menu beside it sat flat.

          The cause is the one `theme.ts` already documents for the Autocomplete popup:
          MUI keys `styleOverrides` on the COMPONENT NAME, and the chrome every dropdown
          gets (`backgroundImage: 'none'`, the divider edge, `shadows[3]`, the 12px
          radius) is written on `MuiMenu.paper`. A `<Popover>` IS NOT A `<Menu>`, so
          none of it ever reached here — including the one property that mattered:
          `backgroundImage` is MUI's dark-mode ELEVATION OVERLAY, a lightening gradient
          painted on every elevated Paper, and nothing was turning it off.

          So both constants are spread, and the values they own are DELETED rather than
          restated beside them:

            menuPaperChromeSx   fill behaviour, edge, elevation, radius
            MENU_PAPER_SX       the 8px trigger gap and the paper insets

          ⚠️ SPREAD ORDER IS LOAD-BEARING. Only this popover's own geometry — width, max
          width, max height, padding — comes after, because those are genuinely its own
          (a 420px form panel, not a row list). `p: 5` intentionally supersedes
          `MENU_PAPER_SX`'s 4px row inset: the shorthand is emitted last, so it wins over
          the longhands, and a form needs card padding rather than a menu's hairline.

          The old `mt: 2` was not merely redundant, it was the drift `MENU_PAPER_SX`'s
          own docstring exists to end: a multiplier renders 8px under the platform theme
          and 16px in `apps/app` (§29), which is why the constant states `'8px'`
          absolute.
        */
        slotProps={{
          paper: {
            sx: {
              ...menuPaperChromeSx(theme),
              ...MENU_PAPER_SX,
              width: 420,
              maxWidth: '90vw',
              maxHeight: '70vh',
              p: 5,
            },
          },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, display: 'block' }}>
          {metricTitle}
        </Typography>
        <Typography
          variant="body3"
          color="text.secondary"
          sx={{ display: 'block', mt: 1, lineHeight: 1.5 }}
        >
          {intro}
        </Typography>

        <Stack spacing={5} sx={{ mt: 5 }}>
          {fields.length === 0 ? (
            <Typography variant="body3" color="text.secondary">
              Loading…
            </Typography>
          ) : (
            fields.map(definition => (
              <MetricInputField
                key={definition.key}
                definition={definition}
                value={valueForKey(values, definition.key)}
                roi={roi}
                isSaving={setInputs.isPending}
                onSave={(stored, currency) => handleSave(definition.key, stored, currency)}
                // The company's CURRENT reporting currency — what a NEW entry defaults
                // to. An existing value keeps its own `currency` and is flagged stale
                // if the two disagree; see `metricInputStale`.
                reportingCurrency={reportingCurrency}
                // The surrounding card IS the metric, so "Used by <this card>" would
                // be restating the heading two lines above it. The Settings page,
                // where a field has no such context, keeps it.
                showUsedBy={false}
                // A 420px panel is not a card: its heading is already the METRIC, so
                // the figure renders as an ordinary field rather than as a titled
                // block. See the prop.
                compact
              />
            ))
          )}
        </Stack>

        {setInputs.isError && (
          <Typography variant="body3" color="error.main" sx={{ display: 'block', mt: 3 }}>
            That could not be saved. Nothing was changed. Check the value and try again.
          </Typography>
        )}

        {savedNote && (
          <Typography variant="body3" color="success.main" sx={{ display: 'block', mt: 3 }}>
            {savedNote}
          </Typography>
        )}

        <Divider sx={{ my: 5 }} />

        {/*
          The way out of the one-metric view. A brand that has just met one of these
          figures usually has the others to hand, and this popover deliberately only
          shows what THIS card is waiting on.
        */}
        <Box>
          {/*
            A real anchor, not a `Button` with an onClick. This is NAVIGATION, so it
            has to survive cmd-click, middle-click and "open in new tab" — all of
            which a button silently swallows. (`@repo/ui`'s Button is also not
            polymorphic: it omits MUI's `component` prop, so the button form was not
            available here even if it were the right control.)
          */}
          <Link
            component={RouterLink}
            to={ROUTES.SETTINGS_COMPANY_BUSINESS_FIGURES}
            state={referrer}
            variant="body3"
            underline="hover"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
          >
            View all figures
            <ArrowUpRightIcon size={16} strokeWidth={1} />
          </Link>
        </Box>
      </Popover>
  )
}

export const MetricInputsNeededButton = ({
  metricId,
  metricTitle,
  missing,
  roi,
  onSaved,
}: MetricInputsPopoverProps) => {
  const theme = useTheme()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  // Per-metric, so two needs-input cards on one canvas do not both claim the same
  // `aria-controls` target.
  const popoverId = `metric-inputs-${metricId}`

  return (
    <>
      {/*
        A TEXT button, not an outlined one. It stands in the slot a 32px figure
        occupies, so a bordered control there would read as the card's primary action
        rather than as a stand-in for a missing number.

        ⚠️ IT MUST NOT BE MISTAKEN FOR THE FIGURE, and the default styling failed that.
        Measured in the browser at 16px / weight 500 / #FAFAFA — the primary text
        colour, in the slot a figure occupies, with a DIGIT first ("1 input needed").
        Skimmed, that reads as a small value rather than as a control. The count stays
        first (a count sets the expectation, and one input is a very different prompt
        from four), so the distinction is carried by ink instead: brand colour, which
        nothing else on a tile uses, at the basis line's 14px rather than the figure's.
      */}
      <Button
        variant="text"
        onClick={e => setAnchor(e.currentTarget)}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
        aria-controls={popoverId}
        sx={{
          px: 0,
          justifyContent: 'flex-start',
          minWidth: 0,
          fontSize: 14,
          color: theme.palette.brand[500],
          '&:hover': { color: theme.palette.brand[400], backgroundColor: 'transparent' },
        }}
      >
        {missing.length} {missing.length === 1 ? 'input' : 'inputs'} needed
      </Button>

      <MetricInputsPanel
        id={popoverId}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        metricId={metricId}
        metricTitle={metricTitle}
        missing={missing}
        intro={
          missing.length === 1
            ? 'Vera has everything else this needs. Supply this figure and the metric activates and starts counting toward your return.'
            : `Vera has everything else this needs. Supply these ${missing.length} figures and the metric activates and starts counting toward your return.`
        }
        roi={roi}
        onSaved={onSaved}
      />
    </>
  )
}

/**
 * The marker beside a figure that RESTS ON A SUPPLIED INPUT, and the way to change it.
 *
 * ## ⚠️ This REVERSES the epic's earlier locked decision, deliberately
 *
 * The rule was "an assumption is stated INLINE, never in a tooltip", on the reasoning
 * that a tooltip is not read by someone who has already believed the number. That
 * reasoning is sound in isolation and it failed in aggregate: the amber caveat line
 * rendered on every assumption-backed card, so a screen of amber sentences beside
 * amber `N inputs needed` pills spent the entire warning budget on the page's NORMAL
 * state. A warning that is always on warns about nothing.
 *
 * What the old rule was protecting is kept, and it is the constraint this component
 * is built around: the disclosure must be VISIBLE WITHOUT HOVER and ADJACENT TO THE
 * FIGURE, so a reader looking at the number sees that it rests on something they
 * supplied. A grey glyph in a card corner would keep the appearance of disclosure
 * while removing the disclosure, which is strictly worse than the amber sentence.
 * Hence: a 24px tinted disc immediately right of the figure, at the figure's own
 * baseline — the one thing on the tile that is not text.
 *
 * The tooltip carries the sentence that used to be inline. The CLICK is what the
 * inline sentence never had: it opens this metric's inputs, so the reader can act on
 * the disclosure where they encountered it rather than being told to go to Settings.
 *
 * ## ⚠️ THE TONE FOLLOWS THE INPUT'S STATE. IT IS NOT ALWAYS AMBER
 *
 * It shipped permanently amber, and permanently amber is the same defect one level
 * down from the one this component was built to fix. Amber is the WARNING tone, and a
 * figure the brand supplied and confirmed has nothing outstanding about it — it is as
 * good as it is ever going to get. Saying "attend to this" about it spends the warning
 * budget on the page's settled state, which is precisely how the inline caveat line
 * came to mean nothing.
 *
 *   confirmed      NEUTRAL (`text.secondary`). The claim is "this rests on a figure
 *                  you supplied", which is worth stating and is not a warning.
 *   unconfirmed    AMBER. A signup-supplied figure nobody has confirmed IS already
 *                  driving the headline, and that genuinely is outstanding.
 *   unset          unreachable here — the card renders `N inputs needed` in the
 *                  figure's slot instead, so there is no figure to mark.
 *
 * The state comes from `metricAssumptionState`, which is built on the SAME
 * `metricInputMarkerState` the Business Figures page counts and pills with. Deriving
 * "is it provisional" locally instead would let this tile and that page disagree about
 * one figure, which on an auditing surface is worse than having neither signal.
 *
 * ## Why the GLYPH does not change with the state
 *
 * `MARKER_GLYPHS` gives the settings page a check / alert / dashed circle per state,
 * and borrowing them here would be wrong in both directions. This disc is a BUTTON
 * that opens the dial — `SlidersHorizontal` names what the click does. A `CircleCheck`
 * in its place asserts the figure is VERIFIED, a much stronger claim than "you
 * supplied it"; a `CircleAlert` asserts something is WRONG with it, which is not the
 * claim either state makes. So colour carries the state and the glyph keeps carrying
 * the affordance, which is the one thing the reader cannot get anywhere else.
 */
export const MetricAssumptionMarker = ({
  metricId,
  metricTitle,
  roi,
  onSaved,
}: Omit<MetricInputsPopoverProps, 'missing'>) => {
  const theme = useTheme()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  /*
    Fetched EAGERLY here, unlike the panel below it, which gates on `anchorEl`.

    The panel can be lazy because nothing about a closed popover depends on the data.
    The marker's COLOUR does, and a tone that only becomes correct once you click the
    thing it is describing is not a signal. Costs one request per page rather than one
    per card: `useMetricInputs` is a react-query hook on a single key, so every marker
    on the canvas reads one cache entry and the panel below now opens against a warm
    one instead of a spinner.
  */
  const { catalogue, values } = useMetricInputs()
  const state = metricAssumptionState(catalogue, values, metricId)
  const unconfirmed = state === 'unconfirmed'

  const popoverId = `metric-assumption-${metricId}`

  /*
    A dim neutral for the settled state, `warning.main` for the outstanding one.

    NOT `brand[500]`, which is what the Business Figures page uses for its `confirmed`
    pill. That page is ABOUT the figures, so a confirmed one is progress through a task
    and reads in the product's colour. Here the figure is not the subject — the metric
    is — and the marker is a footnote on someone else's number, so a brand-purple disc
    beside every assumption-backed figure would be a second violet accent competing
    with the chart's own series for no gain. Neutral is what a footnote looks like.

    ⚠️ AND NOT `theme.palette.text.secondary`, which is what "neutral" reaches for by
    reflex and is a light-mode accessibility bug. That token is `#a1a1aa` in BOTH modes:
    7.47:1 against the dark card measured here, and **2.46:1** on the light surface —
    a marker nobody can see, which is strictly worse than the amber it replaces. This
    repo has already paid for that exact mistake once, in the sidebar's rest-dim
    treatment; `dimNeutralText` is the mode-aware helper that came out of it and returns
    `#52525b` (~7.5:1) in light.
  */
  const tone = unconfirmed ? theme.palette.warning.main : dimNeutralText(theme)

  return (
    <>
      <Tooltip
        arrow
        placement="top"
        title={
          unconfirmed
            ? 'Rests on a figure that came from your signup form and has never been confirmed. It is already driving this number. Open it to confirm or correct it.'
            : 'Rests on a figure you supplied, not only on recorded events. Open it to review or adjust that figure.'
        }
      >
        {/*
          `SlidersHorizontal`, not an alert glyph — see the header. The disclosure is
          "this number has a dial behind it", and the dial is what the click opens.
        */}
        <IconButton
          size="small"
          onClick={e => setAnchor(e.currentTarget)}
          aria-haspopup="dialog"
          aria-expanded={Boolean(anchor)}
          aria-controls={popoverId}
          aria-label={
            unconfirmed
              ? `${metricTitle} rests on an unconfirmed figure — confirm or correct it`
              : `${metricTitle} rests on a figure you supplied — review or adjust it`
          }
          sx={{
            width: 24,
            height: 24,
            flexShrink: 0,
            color: tone,
            // Held at the amber's own alphas rather than lightened for the neutral
            // state. The disc has to stay findable as an OBJECT beside the figure —
            // that visibility-without-hover is the whole constraint this component is
            // built around (see the header), and dropping the fill to make it quieter
            // would arrive back at the grey corner glyph it was designed against.
            backgroundColor: alpha(tone, 0.14),
            border: `1px solid ${alpha(tone, 0.36)}`,
            '&:hover': { backgroundColor: alpha(tone, 0.24) },
          }}
        >
          <SlidersHorizontalIcon size={13} strokeWidth={1.5} />
        </IconButton>
      </Tooltip>

      <MetricInputsPanel
        id={popoverId}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        metricId={metricId}
        metricTitle={metricTitle}
        // No `missing` — the metric HAS its figures, so the panel shows every input it
        // declares rather than an empty list. See `MetricInputsPanelProps.missing`.
        intro="This figure is calculated from a business figure you supplied. Change it here and the metric recalculates."
        roi={roi}
        onSaved={onSaved}
      />
    </>
  )
}

/**
 * A card's figure with its assumption marker beside it.
 *
 * Lives here rather than in `useRoiCards` so the ONE place that restates
 * `AnalyticsBlock`'s figure typography is next to the marker it exists to carry. It
 * is a stand-in for the string `stat` — `AnalyticsBlock` renders an ELEMENT stat in a
 * plain box without the figure's Typography, so the typography has to come from here
 * or the number would render at body size.
 *
 * ⚠️ `statSize` must track `AnalyticsBlock`'s own default. A tile whose figure is a
 * different size from the tile beside it is the defect this composition risks, and it
 * is invisible in a diff.
 */
export const RoiFigureWithAssumption = ({
  figure,
  statSize = 32,
  ...marker
}: Omit<MetricInputsPopoverProps, 'missing'> & {
  figure: string
  statSize?: number
}) => (
  <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
    <Typography
      variant="h4"
      sx={{ fontWeight: 600, fontSize: statSize, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
    >
      {figure}
    </Typography>
    <MetricAssumptionMarker {...marker} />
  </Stack>
)
